/* eslint-disable react/no-danger */
import { useEffect } from 'react';
import type { MouseEvent } from 'react';

import loadable from '@loadable/component';
import BpkIconAI from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/ai';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classnames from 'classnames';
import { redirect } from 'saddlebag-browser';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { anchorScroll } from '../../utils';
import currentLandingPagesOptions from '../../utils/richTextBrandStyles';
import withErrorBoundary from '../ErrorBoundary';
import RichText from '../RichText';
import { useMBDContext } from '../common/MBD/useMBDContext';

import getIcon from './getIcon';

import type { Document } from '@contentful/rich-text-types';
import type {
  ContentBlockColumns,
  ContentBlockProps,
  ContentBlockSectionItem,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ContentBlockProps';

import STYLES from './ContentBlock.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const ContentDescription = ({
  contentIncludesHtmlBlockTags,
  description,
}: {
  description: Document | string | null | undefined;
  contentIncludesHtmlBlockTags: boolean | undefined;
}) => {
  if (!description) return null;
  if (typeof description === 'object' && 'nodeType' in description) {
    return <RichText body={description} options={currentLandingPagesOptions} />;
  }
  return (
    <div className={STYLES.ContentBlock__itemBody}>
      <BpkText
        // Default p tag
        // Only inline elements can be nested within the P tag, and block elements cannot be nested.
        // Sometimes our description needs to use tags to display the format of the paragraph.
        //  p cannot achieve the display effect very well, so we use div as the tag.
        tagName={contentIncludesHtmlBlockTags ? 'div' : 'p'}
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </div>
  );
};

const ContentItem = ({
  MBDIndex,
  contentIncludesHtmlBlockTags,
  description,
  heading,
  icon,
  linkText,
  linkUrl,
}: ContentBlockSectionItem & { MBDIndex: number }) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  return (
    <div key={heading} role="listitem" className={STYLES.ContentBlock__item}>
      {icon && (
        <div className={STYLES.ContentBlock__imgBox}>{getIcon(icon)}</div>
      )}
      <div className={STYLES.ContentBlock__snippet}>
        <BpkText
          tagName="h3"
          textStyle={TEXT_STYLES.label1}
          dangerouslySetInnerHTML={{ __html: heading }}
        />
        <ContentDescription
          description={description}
          contentIncludesHtmlBlockTags={contentIncludesHtmlBlockTags}
        />
        {linkUrl && linkText && (
          <div className={STYLES.ContentBlock__link}>
            <BpkLink
              data-tracking-element-id={`${rawComponentName}_item_link_${MBDIndex}`}
              data-tracking-common-params={buildMBDParam(
                `${rawComponentName}_item_link`,
                {
                  link: linkUrl,
                },
              )}
              href={linkUrl}
              blank
              implicit
            >
              {linkText}
            </BpkLink>
          </div>
        )}
      </div>
    </div>
  );
};

const ContentDisclaimer = ({ disclaimerText }: { disclaimerText: string }) => (
  <div className={STYLES.ContentBlock__disclaimer}>
    <BpkText
      tagName="span"
      textStyle={TEXT_STYLES.sm}
      color={TEXT_COLORS.textSecondary}
      className={STYLES.ContentBlock__disclaimer__aiSummaryFooterText}
    >
      <BpkIconAI
        className={STYLES.ContentBlock__disclaimer__aiSummaryFooterIcon}
      />
      {disclaimerText}
    </BpkText>
  </div>
);

const ContentColumns = ({
  columnCount = 'CONTENT_BLOCK_COLUMNS_TWO',
  content,
}: {
  content: ContentBlockSectionItem[];
  columnCount?: ContentBlockColumns;
}) => {
  const actualColumnCount =
    columnCount === 'CONTENT_BLOCK_COLUMNS_THREE' && content.length > 2 ? 3 : 2;

  const chunkSize = Math.ceil(content.length / actualColumnCount);
  const classNames = [
    STYLES.ContentBlock__itemList,
    STYLES.ContentBlock__columns,
  ];
  if (actualColumnCount === 3) {
    classNames.push(STYLES.ContentBlock__columnsThree);
  }
  const chunks = Array.from({ length: actualColumnCount }, (_, columnIndex) =>
    content.slice(columnIndex * chunkSize, columnIndex * chunkSize + chunkSize),
  );

  return (
    <div role="list" className={classnames(classNames)}>
      {chunks.map((columnItems, columnIndex) => {
        const columnKey =
          columnItems.length > 0
            ? columnItems.map(({ heading }) => heading).join('|')
            : `column-${columnIndex}`;
        return (
          <div className={STYLES.ContentBlock__column} key={columnKey}>
            {columnItems.map((item, index) => (
              <ContentItem key={item.heading} {...item} MBDIndex={index} />
            ))}
          </div>
        );
      })}
    </div>
  );
};

const onContentClick = (e: MouseEvent<HTMLDivElement>) => {
  const target = e.target as HTMLAnchorElement;
  if (target?.href) {
    if (target?.hash?.length) {
      anchorScroll(target?.hash.replace('#', ''));
    } else if (target?.target === '_blank') {
      redirect(target?.href, true);
    } else {
      redirect(target?.href);
    }
  }
};

const ContentBlock = (props: ContentBlockProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  useEffect(() => {
    const container = document.getElementById('seoContentBlock');
    if (!container) return;

    const links = container.querySelectorAll('a'); // if no links, forEach will simply do nothing
    links.forEach((link, index) => {
      if (!link.hasAttribute('data-tracking-element-id')) {
        const elementId = `${rawComponentName}_item_${index}`;
        link.setAttribute('data-tracking-element-id', elementId);

        const commonParams = buildMBDParam(`${rawComponentName}_item`, {
          action: 'click_link',
        });
        link.setAttribute('data-tracking-common-params', commonParams);
      }
    });
  }, [buildMBDParam, rawComponentName]);

  if (!props?.cmsTemplate && !props?.sections?.length) {
    return null;
  }

  if (props?.cmsTemplate) {
    const isTwoColumns = props.columnCount === 'CONTENT_BLOCK_COLUMNS_TWO';
    return (
      <div
        id="seoContentBlock"
        data-testid="content-block"
        data-tracking-element-id={rawComponentName}
        data-tracking-common-params={buildMBDParam(rawComponentName)}
        onClick={(e: MouseEvent<HTMLDivElement>) => {
          const target = e.target as HTMLAnchorElement;
          if (target?.href && target?.hash?.length > 0) {
            e.preventDefault();
          }
          onContentClick(e);
        }}
        role="presentation"
        className={isTwoColumns ? '' : STYLES.ContentBlock}
      >
        <div
          className={classnames({
            [STYLES.ContentBlock__contentfulTemplate]: true,
            [STYLES.ContentBlock__contentfulTemplateTwoColumns]: isTwoColumns,
          })}
          dangerouslySetInnerHTML={{ __html: props?.cmsTemplate }}
        />
      </div>
    );
  }

  return (
    <div
      id="seoContentBlock"
      data-testid="content-block"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      role="presentation"
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLAnchorElement;
        if (target?.href && target?.hash?.length > 0) {
          e.preventDefault();
        }
        onContentClick(e);
      }}
      className={
        props.columnCount && props.columnCount !== 'CONTENT_BLOCK_COLUMNS_ONE'
          ? STYLES.ContentBlockTwoColumns
          : STYLES.ContentBlock
      }
    >
      {props?.sections?.map((section) => {
        if (!section?.sectionItems?.length) {
          return null;
        }

        const showThreeColumns =
          props.columnCount === 'CONTENT_BLOCK_COLUMNS_THREE' &&
          section.sectionItems.length > 2;
        const showTwoColumns =
          props.columnCount === 'CONTENT_BLOCK_COLUMNS_TWO' &&
          section.sectionItems.length > 1 &&
          !showThreeColumns;

        return (
          <div key={section.title} className={STYLES.ContentBlock__container}>
            {section.title && (
              <div className={STYLES.ContentBlock__header}>
                <BpkSectionHeader
                  title={section.title}
                  description={section.subTitle}
                />
              </div>
            )}
            {showThreeColumns || showTwoColumns ? (
              <ContentColumns
                content={section.sectionItems}
                columnCount={props.columnCount}
              />
            ) : (
              <div role="list" className={STYLES.ContentBlock__itemList}>
                {section.sectionItems.map((item, index) => (
                  <ContentItem key={item.heading} {...item} MBDIndex={index} />
                ))}
              </div>
            )}
          </div>
        );
      })}
      {props.disclaimer && (
        <ContentDisclaimer disclaimerText={props.disclaimer} />
      )}
    </div>
  );
};

const ContentBlockWrapper = (props: ContentBlockProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.CONTENT_BLOCK,
      })
    }
  >
    <ContentBlock {...props} />
  </IntersectionObserverWrapper>
);

const ContentBlockWithErrorBoundary = withErrorBoundary(
  ContentBlockWrapper,
  'content-block-component',
);

export default ContentBlockWithErrorBoundary;
